/**
 * Report — status the client sees, report documents, system description,
 * opinion draft.
 *   T6-B10 system description versions with Compare and Accept / Return (a
 *          comment is required to return); document Return takes an inline
 *          reason; the opinion editor starts from the latest version and saves v N+1.
 *   T6-B11 leads set the report date; a representation letter not dated to it
 *          gets "Return: re-date" instead of Accept.
 *   T6-H3  Release is aria-disabled with the reason (and a link to
 *          Exceptions) while Section 4 is locked.
 *   T6-H4  Release opens a pre-release checklist; it confirms only when every
 *          item passes and the client's name is typed. Mark draft sent confirms.
 *   T6-H11 the template notice shows once, details behind "Verify".
 *   T6-H12 one filled primary: "Mark draft sent" (or Release); Accept / Return
 *          are compact outline buttons.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Alert, Card, EmptyState, Field, Input, Select, Textarea } from '@aigovops/design-system';
import { ActionDialog } from '../../components/today/shared';
import { firmApi, type ReportDocument, type SectionDiff, type SystemDescriptionVersion } from '../../lib/firm-api';
import { fmtDate } from '../../lib/format';
import { Chip, GuardedButton, StateBadge, TabHeading, TemplateNotice, useAct, useDraft, useFirm, useFocusLater, useLoad, Loading } from './firm-kit';

const DOC_LABEL: Record<string, string> = {
  management_assertion: 'Management assertion', representation_letter: 'Representation letter', bridge_letter: 'Bridge letter', system_description_review: 'System description review',
};

export function ReportTab() {
  const { slug, eng, lead, writable, lockId } = useFirm();
  const st = useLoad(() => firmApi.reportStatus(slug, eng), [slug, eng]);
  const docs = useLoad(() => firmApi.documents(slug, eng), [slug, eng]);
  const s4 = useLoad(() => firmApi.section4(slug, eng), [slug, eng]);
  const reload = () => { st.reload(); docs.reload(); s4.reload(); };
  const all = docs.data?.documents ?? [];
  const clientDocs = all.filter((d) => d.kind !== 'opinion_draft' && d.kind !== 'system_description_review' && d.status !== 'superseded');
  const opinions = all.filter((d) => d.kind === 'opinion_draft').sort((a, b) => a.version - b.version);
  return (
    <div className="gp-firm-stack">
      <TabHeading id="report-h">Report</TabHeading>
      <Loading l={st}>{(s) => <StatusBar status={s.status} label={s.label} locked={!!s4.data?.locked} docs={clientDocs} onChanged={reload} />}</Loading>
      {lead ? <ReportDate /> : null}
      <section aria-labelledby="docs-h" className="gp-firm-section">
        <h3 id="docs-h" className="gp-firm-h3">Documents from the client</h3>
        <TemplateNotice detail={clientDocs.find((d) => d.template_review_status)?.template_review_status} />
        <Loading l={docs}>{() => clientDocs.length === 0 ? <EmptyState title="Nothing submitted yet" description="The client drafts the assertion and letters on its engagement page." /> : (
          <ul className="gp-firm-list">{clientDocs.map((d) => <DocumentRow key={d.doc_id} d={d} onChanged={reload} />)}</ul>
        )}</Loading>
      </section>
      <SystemDescriptions />
      {lead ? <OpinionEditor versions={opinions} onSaved={reload} /> : <p className="gp-firm-muted">The engagement partner or manager drafts the opinion.</p>}
      {!writable ? <p className="gp-firm-muted" aria-describedby={lockId ?? undefined}>Report decisions are closed.</p> : null}
    </div>
  );
}

function StatusBar({ status, label, locked, docs, onChanged }: { status: string; label: string; locked: boolean; docs: ReportDocument[]; onChanged: () => void }) {
  const { slug, eng, o, lead, writable } = useFirm();
  const act = useAct(onChanged);
  const [confirmDraft, setConfirmDraft] = useState(false);
  const [release, setRelease] = useState(false);
  const canAct = lead && writable;
  return (
    <div className="gp-firm-actions gp-firm-statusbar">
      <span>Status the client sees: <Chip tone={status === 'issued' ? 'success' : 'neutral'}>{label}</Chip></span>
      {act.error ? <Alert variant="error">{act.error}</Alert> : null}
      {canAct && status === 'fieldwork' ? <GuardedButton variant="primary" busy={act.busy} onClick={() => setConfirmDraft(true)}>Mark draft sent</GuardedButton> : null}
      {canAct && status === 'draft_sent' ? (
        <GuardedButton variant="primary" blocked={locked} busy={act.busy} reasonId="release-why"
          reason={locked ? <>Section 4 is locked · <a href="?tab=exceptions">See exceptions</a></> : null}
          onClick={() => setRelease(true)}>Release the report…</GuardedButton>
      ) : null}
      {!lead && status !== 'issued' ? <span className="gp-firm-why">The partner or manager releases the report.</span> : null}
      <ActionDialog open={confirmDraft} title="Mark the draft as sent?" submitLabel="Mark draft sent" busy={act.busy} error={act.error}
        onClose={() => setConfirmDraft(false)}
        onSubmit={async () => { if (await act.call('setReportStatus', [slug, eng, 'draft_sent'], 'Report status: Draft sent')) setConfirmDraft(false); }}>
        <p>{o.client.name} sees “Draft sent”. The status only moves forward.</p>
      </ActionDialog>
      {release ? <ReleaseChecklist docs={docs} locked={locked} onClose={() => setRelease(false)} onReleased={onChanged} /> : null}
    </div>
  );
}

/** T6-H4: every item must pass and the client's name be typed before the report is released. */
function ReleaseChecklist({ docs, locked, onClose, onReleased }: { docs: ReportDocument[]; locked: boolean; onClose: () => void; onReleased: () => void }) {
  const { slug, eng, o } = useFirm();
  const tests = useLoad(() => firmApi.tests(slug, eng), [slug, eng]);
  const act = useAct(onReleased);
  const [typed, setTyped] = useState('');
  const rows = tests.data?.tests ?? [];
  const reviewed = rows.filter((t) => t.status === 'reviewed').length;
  const notes = rows.reduce((n, t) => n + t.open_notes, 0);
  const rep = docs.find((d) => d.kind === 'representation_letter');
  const checks = [
    { ok: rows.length > 0 && reviewed === rows.length, text: `Tests reviewed ${reviewed}/${rows.length}` },
    { ok: notes === 0, text: notes === 0 ? 'Review notes cleared' : `${notes} review note${notes === 1 ? '' : 's'} open` },
    { ok: !locked, text: locked ? 'Section 4 locked' : 'Section 4 consistent' },
    { ok: !!rep && rep.status === 'accepted' && !rep.stale, text: !rep ? 'No representation letter' : rep.stale ? 'Representation letter not dated to the report' : rep.status === 'accepted' ? 'Representation letter accepted and dated' : 'Representation letter not accepted' },
  ];
  const nameOk = typed.trim() === o.client.name;
  const ready = checks.every((c) => c.ok) && nameOk;
  return (
    <ActionDialog open title="Release the report" submitLabel="Release" busy={act.busy} error={act.error} submitDisabled={!ready} onClose={onClose}
      onSubmit={async () => { if (await act.call('setReportStatus', [slug, eng, 'issued'], 'Report released · the file is read-only for assembly')) onClose(); }}>
      <ul className="gp-firm-checklist" aria-label="Before release">
        {checks.map((c) => <li key={c.text} className={c.ok ? 'gp-firm-ok' : 'gp-firm-lock'}><span aria-hidden="true">{c.ok ? '✓ ' : '✕ '}</span>{c.text}<span className="gp-sr-only">{c.ok ? ' (done)' : ' (not done)'}</span></li>)}
      </ul>
      <Field id="release-name" label={`Type “${o.client.name}” to release`} hint="After release the working papers are read-only for the assembly window.">
        <Input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
      </Field>
    </ActionDialog>
  );
}

function ReportDate() {
  const { slug, eng, o, writable } = useFirm();
  const act = useAct(() => undefined);
  const [v, setV] = useState(o.engagement.report_date ?? '');
  const [saved, setSaved] = useState(o.engagement.report_date);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v) || v === saved) return;
    if (await act.call('setReportDate', [slug, eng, v], `Report date set to ${fmtDate(v)}`)) setSaved(v);
  };
  if (!writable) return null;
  return (
    <form className="gp-firm-form gp-firm-form--row" onSubmit={submit} noValidate>
      {act.error ? <Alert variant="error">{act.error}</Alert> : null}
      <Field id="report-date" label="Report date" hint="The representation letter must carry this date.">
        <Input type="date" value={v} onChange={(e) => setV(e.target.value)} />
      </Field>
      <div className="gp-firm-actions"><GuardedButton type="submit" compact busy={act.busy} blocked={!v || v === saved} reason={v === saved ? 'Unchanged' : undefined}>Save date</GuardedButton></div>
    </form>
  );
}

function DocumentRow({ d, onChanged }: { d: ReportDocument; onChanged: () => void }) {
  const { slug, eng, writable } = useFirm();
  const act = useAct(onChanged);
  const focus = useFocusLater();
  const [returning, setReturning] = useState(false);
  const [why, setWhy] = useDraft(`doc-ret-${d.doc_id}`, '');
  const [tried, setTried] = useState(false);
  const label = DOC_LABEL[d.kind] ?? d.kind;
  const doReturn = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (why.trim().length < 5) return;
    if (await act.call('decideDocument', [slug, eng, d.doc_id, 'return', why.trim()], `${label} returned`)) { setReturning(false); setWhy(''); focus(`doc-${d.doc_id}`); }
  };
  return (
    <li className="gp-firm-row" id={`doc-${d.doc_id}`} tabIndex={-1}>
      <div className="gp-firm-row-head">
        <h4 className="gp-firm-row-title">{label} · v{d.version}</h4>
        {d.stale ? <Chip tone="danger">Not dated to the report</Chip> : <StateBadge state={d.status} label={d.status === 'submitted' ? 'Submitted · review' : undefined} />}
      </div>
      {d.document_date ? <p className="gp-firm-muted">Dated {fmtDate(d.document_date)}</p> : null}
      {d.return_comment ? <p className="gp-firm-muted">Returned: {d.return_comment}</p> : null}
      {act.error ? <Alert variant="error">{act.error}</Alert> : null}
      {d.status === 'submitted' && writable ? (
        returning ? (
          <form className="gp-firm-form" onSubmit={doReturn} noValidate>
            <Field id={`doc-why-${d.doc_id}`} label="What needs changing" hint="The client sees this." error={tried && why.trim().length < 5 ? 'Give a reason (at least 5 characters).' : null}>
              <Input value={why} onChange={(e) => setWhy(e.target.value)} />
            </Field>
            <div className="gp-firm-actions">
              <GuardedButton type="submit" compact busy={act.busy}>Return {label.toLowerCase()}</GuardedButton>
              <GuardedButton compact variant="ghost" onClick={() => setReturning(false)}>Cancel</GuardedButton>
            </div>
          </form>
        ) : d.stale ? (
          <div className="gp-firm-actions">
            <GuardedButton compact busy={act.busy} aria-label={`Return the ${label.toLowerCase()} to be re-dated`}
              onClick={() => void act.call('decideDocument', [slug, eng, d.doc_id, 'return', 'Please re-date the letter to the report date and resend.'], `${label} returned to be re-dated`)}>Return: re-date</GuardedButton>
          </div>
        ) : (
          <div className="gp-firm-actions">
            <GuardedButton compact busy={act.busy} aria-label={`Accept ${label.toLowerCase()} v${d.version}`} onClick={() => void act.call('decideDocument', [slug, eng, d.doc_id, 'accept'], `${label} accepted`)}>Accept</GuardedButton>
            <GuardedButton compact aria-label={`Return ${label.toLowerCase()} v${d.version}…`} onClick={() => { setReturning(true); focus(`doc-why-${d.doc_id}`); }}>Return…</GuardedButton>
          </div>
        )
      ) : null}
    </li>
  );
}

function SystemDescriptions() {
  const { slug, eng, writable } = useFirm();
  const l = useLoad(() => firmApi.sdVersions(slug, eng), [slug, eng]);
  const versions = l.data?.versions ?? [];
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [diff, setDiff] = useState<{ from_version: number; to_version: number; sections: SectionDiff[] } | null>(null);
  const act = useAct(l.reload);
  useEffect(() => {
    if (versions.length >= 2 && !from && !to) { setFrom(versions[versions.length - 2]!.description_id); setTo(versions[versions.length - 1]!.description_id); }
  }, [versions, from, to]);
  const compare = (e: FormEvent) => {
    e.preventDefault();
    if (!from || !to || from === to) return;
    void act.run(async () => setDiff(await firmApi.sdDiff(slug, eng, from, to)));
  };
  return (
    <section aria-labelledby="sd-h" className="gp-firm-section">
      <h3 id="sd-h" className="gp-firm-h3">System description</h3>
      {act.error ? <Alert variant="error">{act.error}</Alert> : null}
      <Loading l={l}>{() => versions.length === 0 ? <EmptyState title="No system description for this period yet" description="The client approves one in its SOC 2 program." /> : (
        <>
          <ul className="gp-firm-list">{[...versions].reverse().map((v) => <SdRow key={v.description_id} v={v} canReview={writable} onChanged={l.reload} />)}</ul>
          {versions.length >= 2 ? (
            <form className="gp-firm-form gp-firm-form--row" onSubmit={compare} noValidate>
              <Field id="sd-from" label="Compare"><Select value={from} onChange={(e) => setFrom(e.target.value)}>{versions.map((v) => <option key={v.description_id} value={v.description_id}>v{v.version}</option>)}</Select></Field>
              <Field id="sd-to" label="with"><Select value={to} onChange={(e) => setTo(e.target.value)}>{versions.map((v) => <option key={v.description_id} value={v.description_id}>v{v.version}</option>)}</Select></Field>
              <div className="gp-firm-actions"><GuardedButton type="submit" compact busy={act.busy} blocked={from === to} reason={from === to ? 'Pick two versions' : undefined}>Compare</GuardedButton></div>
            </form>
          ) : null}
          {diff ? (
            <div className="gp-firm-diff" aria-label={`Changes from v${diff.from_version} to v${diff.to_version}`}>
              {diff.sections.filter((s) => s.change !== 'unchanged').map((s) => (
                <div key={s.key}>
                  <p className="gp-firm-h4">{s.title} · {s.change}</p>
                  <ul>{s.lines.filter((x) => x.op !== 'same').map((x, i) => <li key={i} className={x.op === 'add' ? 'gp-firm-diff-add' : 'gp-firm-diff-del'}><span aria-hidden="true">{x.op === 'add' ? '+ ' : '− '}</span><span className="gp-sr-only">{x.op === 'add' ? 'Added: ' : 'Removed: '}</span>{x.text}</li>)}</ul>
                </div>
              ))}
              {diff.sections.every((s) => s.change === 'unchanged') ? <p className="gp-firm-muted">No changes.</p> : null}
            </div>
          ) : null}
        </>
      )}</Loading>
    </section>
  );
}

function SdRow({ v, canReview, onChanged }: { v: SystemDescriptionVersion; canReview: boolean; onChanged: () => void }) {
  const { slug, eng } = useFirm();
  const act = useAct(onChanged);
  const [returning, setReturning] = useState(false);
  const [why, setWhy] = useDraft(`sd-ret-${v.description_id}`, '');
  const [tried, setTried] = useState(false);
  const decided = v.review?.status;
  const doReturn = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (why.trim().length < 5) return;
    if (await act.call('reviewSd', [slug, eng, v.description_id, 'return', why.trim()], `System description v${v.version} returned`)) { setReturning(false); setWhy(''); }
  };
  return (
    <li className="gp-firm-row">
      <div className="gp-firm-row-head">
        <h4 className="gp-firm-row-title">Version {v.version}</h4>
        {decided ? <StateBadge state={decided} /> : v.status === 'approved' ? <Chip tone="accent">Review</Chip> : <Chip>{v.status === 'superseded' ? 'Superseded' : 'Draft'}</Chip>}
      </div>
      <p className="gp-firm-muted">{v.approved_at ? `Approved by the client ${fmtDate(v.approved_at)}` : `Created ${fmtDate(v.created_at)}`}{v.review?.comment ? ` · your comment: ${v.review.comment}` : ''}</p>
      {act.error ? <Alert variant="error">{act.error}</Alert> : null}
      {canReview && v.status === 'approved' && !decided ? (
        returning ? (
          <form className="gp-firm-form" onSubmit={doReturn} noValidate>
            <Field id={`sd-why-${v.description_id}`} label="Comment for the client" error={tried && why.trim().length < 5 ? 'A comment is required to return it.' : null}>
              <Textarea rows={2} value={why} onChange={(e) => setWhy(e.target.value)} />
            </Field>
            <div className="gp-firm-actions">
              <GuardedButton type="submit" compact busy={act.busy}>Return v{v.version}</GuardedButton>
              <GuardedButton compact variant="ghost" onClick={() => setReturning(false)}>Cancel</GuardedButton>
            </div>
          </form>
        ) : (
          <div className="gp-firm-actions">
            <GuardedButton compact busy={act.busy} aria-label={`Accept system description v${v.version}`} onClick={() => void act.call('reviewSd', [slug, eng, v.description_id, 'accept'], `System description v${v.version} accepted`)}>Accept</GuardedButton>
            <GuardedButton compact aria-label={`Return system description v${v.version}…`} onClick={() => setReturning(true)}>Return…</GuardedButton>
          </div>
        )
      ) : null}
    </li>
  );
}

function OpinionEditor({ versions, onSaved }: { versions: ReportDocument[]; onSaved: () => void }) {
  const { slug, eng, writable } = useFirm();
  const act = useAct(onSaved);
  const latest = versions[versions.length - 1];
  const [body, setBody] = useDraft('opinion', latest?.body ?? '');
  const [base, setBase] = useState(latest?.doc_id);
  useEffect(() => { if (latest && latest.doc_id !== base) { setBody(latest.body); setBase(latest.doc_id); } }, [latest, base, setBody]);
  const [tried, setTried] = useState(false);
  const next = (latest?.version ?? 0) + 1;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (body.trim().length < 10 || body.trim() === latest?.body.trim()) return;
    if (await act.call('saveOpinion', [slug, eng, body.trim()], `Opinion draft v${next} saved`)) setTried(false);
  };
  return (
    <Card title="Opinion draft · firm only">
      {act.error ? <Alert variant="error">{act.error}</Alert> : null}
      {writable ? (
        <form className="gp-firm-form gp-firm-form--wide" onSubmit={submit} noValidate>
          <Field id="opinion" label={latest ? `Editing from v${latest.version}` : 'First draft'} error={tried && body.trim().length < 10 ? 'Write the draft (at least 10 characters).' : tried && body.trim() === latest?.body.trim() ? 'No changes to save.' : null}>
            <Textarea rows={6} value={body} onChange={(e) => setBody(e.target.value)} />
          </Field>
          <div className="gp-firm-actions"><GuardedButton type="submit" busy={act.busy}>Save as v{next}</GuardedButton></div>
        </form>
      ) : latest ? <p className="gp-firm-pre">{latest.body}</p> : null}
      {versions.length ? (
        <details className="gp-firm-history">
          <summary>History ({versions.length})</summary>
          <ol>{[...versions].reverse().map((v) => <li key={v.doc_id}><strong>v{v.version}</strong> · {fmtDate(v.created_at)}<p className="gp-firm-pre">{v.body}</p></li>)}</ol>
        </details>
      ) : null}
    </Card>
  );
}
